<template>
  <div>
    <v-row dense>
      <v-col :cols="view === 'player' ? 2 : 3">
        <v-card class="rounded-lg fill-height">
          <v-card-title>
            {{ $t('variables.player_wellness.long') }}
          </v-card-title>
          <v-card-text class="text-center pt-6">
            <v-progress-circular
              v-if="stats"
              :value="stats.player_wellness"
              :size="150"
              :width="16"
              :rotate="-90"
              :color="getColorForValue(stats.player_wellness, 100)"
            >
              <div class="text-h4 grey--text text--darken-2">
                {{ stats.player_wellness }}%
              </div>
            </v-progress-circular>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col :cols="view === 'player' ? 2 : 3">
        <v-row
          no-gutters
          class="fill-height d-flex flex-column"
        >
          <v-col>
            <stat-card
              :title="$t('variables.SLD.long')"
              :loading="loading"
              :value="stats.SLD"
              :max="9"
              :color="getColorForValue(stats.SLD, 8)"
            >
              {{ stats.SLD }} h
            </stat-card>
          </v-col>
          <v-col class="pt-2">
            <stat-card
              :title="$t('table_headers.player_overview.sickness')"
              :loading="loading"
              value-text="true"
              class="fill-height"
            >
              <template v-slot:default>
                <div
                  v-if="stats.sick"
                  class="text-h6 mt-4 error--text"
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <span
                        v-bind="attrs"
                        class="cursor-pointer"
                        v-on="on"
                      >
                        {{ stats.symptoms.length }} symptoms
                      </span>
                    </template>
                    <div class="tooltip-content">
                      <div
                        v-for="(symptom, index) in stats.symptoms"
                        :key="index"
                      >
                        {{ $t(`symptoms.${toCamelCase(symptom.symptom)}`) }}
                      </div>
                    </div>
                  </v-tooltip>
                </div>
                <div
                  v-else
                  class="text-h6 mt-4 success--text"
                >
                  {{ $t("status.sickness.no_symptoms") }}
                </div>
              </template>
            </stat-card>
          </v-col>
        </v-row>
      </v-col>
      <v-col v-if="view === 'player'">
        <v-card class="rounded-lg fill-height">
          <v-card-title>
            {{ $t('variables.pain.long') }}
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col class="justify-center d-flex">
                <human-body-female-front-muscles
                  v-if="gender === 'female'"
                  style="height: 200px; width: 120px"
                  :stats="stats"
                  stats-suffix="_DL"
                />
                <human-body-front-muscles
                  v-else
                  style="height: 200px; width: 120px"
                  :stats="stats"
                  stats-suffix="_DL"
                />
              </v-col>
              <v-col class="justify-center d-flex">
                <human-body-female-back-muscles
                  v-if="gender === 'female'"
                  style="height: 200px; width: 120px"
                  :stats="stats"
                  stats-suffix="_DL"
                />
                <human-body-back-muscles
                  v-else
                  style="height: 200px; width: 120px"
                  :stats="stats"
                  stats-suffix="_DL"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col :cols="view === 'player' ? 2 : 3">
        <v-row dense>
          <v-col cols="12">
            <stat-card
              :title="$t('variables.MOO.long')"
              :loading="loading"
              :value="stats.MOO"
              :max="5"
              :color="getColorForValue(stats.MOO, 5)"
            />
          </v-col>
          <v-col cols="12">
            <stat-card
              :title="$t('variables.STL.long')"
              :loading="loading"
              :value="stats.STL"
              :max="5"
              :color="getColorForValue(stats.STL, 5, true)"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col :cols="view === 'player' ? 2 : 3">
        <v-row dense>
          <v-col cols="12">
            <stat-card
              :title="$t('variables.FAT.long')"
              :loading="loading"
              :value="stats.FAT"
              :max="5"
              :color="getColorForValue(stats.FAT, 5, true)"
            />
          </v-col>
          <v-col cols="12">
            <stat-card
              :title="$t('variables.SLQ.long')"
              :loading="loading"
              :value="stats.SLQ"
              :max="5"
              :color="getColorForValue(stats.SLQ, 5)"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row
      v-if="gender === 'female'"
      dense
    >
      <v-col>
        <v-card class="rounded-lg fill-height">
          <v-card-title>
            <div class="text-h6 font-weight-medium">
              {{ $t("card_title.menstrual_cycle") }}
            </div>
          </v-card-title>
          <v-card-text
            class="pa-6"
            style="height: 80%"
          >
            <v-row
              align="center"
              no-gutters
              class="fill-height"
            >
              <v-col>
                <donut-chart
                  :series="chartSeries"
                  :labels="chartLabels"
                  :colors="chartColors"
                  :center-value="`${$t('text_input.data.day.title')} ${stats.cycle_length}`"
                  :center-text="flowRate"
                  height="260"
                  donut-size="70%"
                  :show-legend="false"
                  :hide-labels="true"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col>
        <v-card class="rounded-lg fill-height">
          <v-card-title>
            <div class="text-h6 font-weight-medium">
              {{ $t("card_title.menstrual_phases_forecast") }}
            </div>
          </v-card-title>
          <v-card-text
            style="height: 80%"
            class="d-flex flex-column justify-space-between"
          >
            <div>
              <v-row
                v-for="phase in menstrualPhases"
                :key="'tooltip-' + phase.key"
                :class="{ 'font-weight-bold': stats.cycle_phase === phase.key }"
                class="text-subtitle-1"
                dense
              >
                <v-col cols="auto">
                  {{ phase.text }}
                </v-col>
                <v-spacer />
                <v-col
                  v-if="phase.fromDate && phase.toDate"
                  cols="auto"
                >
                  {{ phase.fromDate }} - {{ phase.toDate }}
                </v-col>
                <v-col
                  v-else-if="phase.fromDate"
                  cols="auto"
                >
                  {{ phase.fromDate }} - ...
                </v-col>
                <v-col
                  v-else-if="phase.from && phase.to"
                  cols="auto"
                >
                  {{ phase.from }}. - {{ phase.to }}.
                </v-col>
                <v-col
                  v-else-if="phase.from"
                  cols="auto"
                >
                  {{ phase.from }}. - ...
                </v-col>
              </v-row>
            </div>
            <div
              class="grey--text"
              v-html="$t('wellness.menstrual_cycle.next_period', {
                date: $dayjs(stats.estimated_cycle_end).format('MMMM D')
              })"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <v-col>
        <v-card class="rounded-lg fill-height">
          <v-card-title>
            <div class="d-flex align-center">
              <span class="text-h6 font-weight-medium">
                {{ $t("wellness.menstrual_phase.characteristics", {
                  phase: $t(`menstrual_cycle.${stats.cycle_phase}`)
                }) }}
              </span>
            </div>
          </v-card-title>
          <v-card-text>
            <div class="text-h6 font-weight-medium">
              {{ $t('wellness.hormonal_profile') }}
            </div>
            <div class="pa-2  text-body-1">
              {{ $t(`smartAssistant.itl.womenHealth.${stats.cycle_phase}.hormones`) }}.
            </div>
            <div class="mt-2">
              <span class="text-h6 font-weight-medium">{{ $t('wellness.cognitive_and_physical') }}</span>
            </div>
            <div class="pa-2  text-body-1">
              {{ $t(`smartAssistant.itl.womenHealth.${stats.cycle_phase}.status`) }}
            </div>

            <div class="mt-2">
              <span class="text-h6 font-weight-medium">{{ $t('wellness.recommendations') }}</span>
              <div class="pa-2 text-body-1">
                {{ $t(`smartAssistant.itl.womenHealth.${stats.cycle_phase}.recommendation`) }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import HumanBodyFemaleBackMuscles from '@/components/human-body/HumanBodyFemaleBackMuscles.vue'
import HumanBodyFemaleFrontMuscles from '@/components/human-body/HumanBodyFemaleFrontMuscles.vue'
import StatCard from '@/components/StatCard.vue'
import HumanBodyFrontMuscles from '@/components/human-body/HumanBodyFrontMuscles.vue'
import HumanBodyBackMuscles from '@/components/human-body/HumanBodyBackMuscles.vue'
import DonutChart from '@/components/charts/DonutChart.vue'
import { getColorForValue, toCamelCase } from '@/utils/helpers'
import { MenstrualPhases } from '@/data/models'
import dayjs from 'dayjs'

export default {
  name: 'TodayWellness',
  components: {
    HumanBodyBackMuscles,
    HumanBodyFrontMuscles,
    StatCard,
    HumanBodyFemaleFrontMuscles,
    HumanBodyFemaleBackMuscles,
    DonutChart
  },
  props: {
    stats: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    gender: {
      type: String,
      default: 'male'
    },
    view: {
      type: String,
      default: 'player'
    }
  },
  computed: {
    chartSeries() {
      if (!this.stats || this.gender !== 'female') return []

      return [
        this.stats.menstruation_length || 0,
        this.stats.early_follicular || 0,
        this.stats.mid_follicular || 0,
        this.stats.late_follicular || 0,
        this.stats.ovulation || 0,
        this.stats.early_luteal || 0,
        this.stats.mid_luteal || 0,
        this.stats.late_luteal || 0,
        this.stats.period_overdue || 0
      ].filter(value => value > 0)
    },
    chartLabels() {
      const labels = []
      if (this.stats.menstruation_length > 0) labels.push('Menstruation')
      if (this.stats.early_follicular > 0) labels.push('Early Follicular')
      if (this.stats.mid_follicular > 0) labels.push('Mid Follicular')
      if (this.stats.late_follicular > 0) labels.push('Late Follicular')
      if (this.stats.ovulation > 0) labels.push('Ovulation')
      if (this.stats.early_luteal > 0) labels.push('Early Luteal')
      if (this.stats.mid_luteal > 0) labels.push('Mid Luteal')
      if (this.stats.late_luteal > 0) labels.push('Late Luteal')
      if (this.stats.period_overdue > 0) labels.push('Period Overdue')

      return labels
    },
    chartColors() {
      const colors = []
      const activePhase = this.stats.cycle_phase

      if (this.stats.menstruation_length > 0) {
        colors.push(activePhase === 'menstruation_length' ? '#a12828' : '#a1282870')
      }
      if (this.stats.early_follicular > 0) {
        colors.push(activePhase === 'early_follicular' ? '#F35F4F' : '#F35F4F70')
      }
      if (this.stats.mid_follicular > 0) {
        colors.push(activePhase === 'mid_follicular' ? '#FFE4E2' : '#FFE4E270')
      }
      if (this.stats.late_follicular > 0) {
        colors.push(activePhase === 'late_follicular' ? '#F8DEF7' : '#F8DEF770')
      }
      if (this.stats.ovulation > 0) {
        colors.push(activePhase === 'ovulation' ? '#f8f8f8' : '#f8f8f870')
      }
      if (this.stats.early_luteal > 0) {
        colors.push(activePhase === 'early_luteal' ? '#746EB5' : '#746EB570')
      }
      if (this.stats.mid_luteal > 0) {
        colors.push(activePhase === 'mid_luteal' ? '#583493' : '#58349370')
      }
      if (this.stats.late_luteal > 0) {
        colors.push(activePhase === 'late_luteal' ? '#363E86' : '#363E8670')
      }

      return colors
    },
    flowRate() {
      if (!this.stats.FMENSACT) return ''
      if (this.stats.FMENSFR === 1) return 'Low flow rate'
      if (this.stats.FMENSFR === 2) return 'Medium flow rate'
      if (this.stats.FMENSFR === 3) return 'High flow rate'

      return ''
    }
  },
  watch: {
    stats() {
      const keys = Object.keys(MenstrualPhases)
      const menstrualPhases = []

      let startDate = null

      startDate = dayjs(this.stats.cycle_start)

      for (const key of keys) {
        const phase = MenstrualPhases[key]

        phase.key = key
        phase.duration = this.stats[key] || 0

        if (startDate) {
          phase.fromDate = startDate.format('DD/MM')
          phase.toDate = startDate.clone().add(phase.duration - 1, 'days').format('DD/MM')

          startDate = startDate.add(phase.duration, 'days')
        }

        if (phase.key === 'period_overdue' && this.stats.cycle_phase !== 'period_overdue') {
          continue
        }
        menstrualPhases.push(phase)
      }

      this.menstrualPhases = menstrualPhases
    }
  },
  created() {
    this.menstrualPhases = []
  },
  methods: {
    toCamelCase,
    getColorForValue
  }
}
</script>

<style scoped>
</style>
